/**
 * Profit Calculator Component
 * Displays profit analysis for all payment methods based on desired profit
 */

import { CreditCard, Wallet, AlertTriangle, Star, DollarSign } from 'lucide-react';
import type { ProfitAnalysis, ProfitCalculation } from '../types';
import { PaymentMethod } from '../types';
import { CurrencyInput } from './CurrencyInput';
import {
  formatCurrency,
  formatPercentage,
  getProfitColorClass,
} from '../utils/calculations';

interface ProfitCalculatorProps {
  profitAnalysis: ProfitAnalysis | null;
  desiredChargeAmount: number;
  onDesiredChargeChange: (amount: number) => void;
  airlinePrice: number;
}

export function ProfitCalculator({
  profitAnalysis,
  desiredChargeAmount,
  onDesiredChargeChange,
  airlinePrice,
}: ProfitCalculatorProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 rounded-lg bg-emerald-500/10 text-emerald-500">
            <DollarSign className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-zinc-100">
              Calculadora de Preço de Venda
            </h3>
            <p className="text-sm text-zinc-400">
              Defina o lucro desejado e veja o preço de venda para cada forma de pagamento
            </p>
          </div>
        </div>

        <CurrencyInput
          label="Quanto de lucro deseja?"
          value={desiredChargeAmount}
          onChange={onDesiredChargeChange}
          placeholder="0,00"
          required
          className="max-w-md"
        />
      </div>

      {/* Results */}
      {profitAnalysis && (
        <div className="space-y-4">
          {/* PIX */}
          <PaymentMethodCard
            profit={profitAnalysis.pix}
            isBest={profitAnalysis.bestOption.paymentMethod === PaymentMethod.PIX}
            airlinePrice={airlinePrice}
          />

          {/* Card 1x */}
          <PaymentMethodCard
            profit={profitAnalysis.card1x}
            isBest={profitAnalysis.bestOption.paymentMethod === PaymentMethod.CARD_1X}
            airlinePrice={airlinePrice}
          />

          {/* Card Installments */}
          {profitAnalysis.cardInstallments.length > 0 ? (
            <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-zinc-100">
                    Cartão Parcelado
                  </h4>
                  <p className="text-xs text-zinc-400">
                    Parcelas disponíveis - Taxa de 3.99% por parcela
                  </p>
                </div>
              </div>

              {/* Installment Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {profitAnalysis.cardInstallments.map((profit) => (
                  <InstallmentCard
                    key={profit.installments}
                    profit={profit}
                    isBest={
                      profitAnalysis.bestOption.paymentMethod === PaymentMethod.CARD_INSTALLMENTS &&
                      profitAnalysis.bestOption.installments === profit.installments
                    }
                    airlinePrice={airlinePrice}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-yellow-400 mb-1">
                    Nenhuma opção de parcelamento disponível
                  </h4>
                  <p className="text-xs text-yellow-400/80">
                    Com o lucro desejado de {formatCurrency(desiredChargeAmount)}, o preço necessário para parcelamentos ultrapassaria o valor da companhia aérea.
                    Considere reduzir o lucro desejado ou use PIX/Cartão 1x.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!profitAnalysis && desiredChargeAmount > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-3" />
          <p className="text-sm text-yellow-400">
            Complete todos os campos da cotação para ver a análise de lucro
          </p>
        </div>
      )}
    </div>
  );
}

// Payment Method Card Component
function PaymentMethodCard({
  profit,
  isBest,
  airlinePrice,
}: {
  profit: ProfitCalculation;
  isBest: boolean;
  airlinePrice: number;
}) {
  const icon = profit.paymentMethod === PaymentMethod.PIX ? Wallet : CreditCard;
  const Icon = icon;

  // Calcular economia do cliente
  const customerSavings = Math.max(0, airlinePrice - profit.chargedAmount);
  const savingsPercentage = airlinePrice > 0 ? (customerSavings / airlinePrice) * 100 : 0;

  const getMethodName = () => {
    if (profit.paymentMethod === PaymentMethod.PIX) return 'PIX';
    if (profit.paymentMethod === PaymentMethod.CARD_1X) return 'Cartão 1x';
    return `Cartão ${profit.installments}x`;
  };

  const getFeeText = () => {
    if (profit.paymentMethod === PaymentMethod.PIX) return 'Sem taxas - melhor opção para o cliente';
    return `Taxa de ${formatPercentage(profit.feePercentage * 100)} embutida no preço`;
  };

  return (
    <div
      className={`relative bg-zinc-900/40 border rounded-xl p-5 transition-all ${
        isBest
          ? 'border-emerald-500/50 shadow-lg shadow-emerald-500/10'
          : profit.exceedsAirlinePrice
          ? 'border-red-500/30'
          : 'border-zinc-800/50'
      }`}
    >
      {isBest && (
        <div className="absolute -top-3 left-4 px-3 py-1 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full flex items-center gap-1.5">
          <Star className="w-3.5 h-3.5 text-white fill-white" />
          <span className="text-xs font-semibold text-white">Recomendado</span>
        </div>
      )}

      <div className="flex items-start justify-between gap-4">
        {/* Left side */}
        <div className="flex items-start gap-3 flex-1">
          <div className={`p-2 rounded-lg ${
            profit.paymentMethod === PaymentMethod.PIX
              ? 'bg-emerald-500/10 text-emerald-500'
              : 'bg-blue-500/10 text-blue-500'
          }`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="space-y-2 flex-1">
            <div>
              <h4 className="text-base font-semibold text-zinc-100">
                {getMethodName()}
              </h4>
              <p className="text-xs text-zinc-400">{getFeeText()}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-zinc-400 text-xs">Você Recebe</p>
                <p className="text-emerald-400 font-semibold">
                  {formatCurrency(profit.receivedAmount)}
                </p>
              </div>
              <div>
                <p className="text-zinc-400 text-xs">Taxa</p>
                <p className="text-zinc-200 font-medium">
                  {formatCurrency(profit.feeAmount)}
                </p>
              </div>
              <div>
                <p className="text-zinc-400 text-xs">Lucro Líquido</p>
                <p className={`font-semibold ${
                  getProfitColorClass(profit.grossProfit, profit.isProfitable)
                }`}>
                  {formatCurrency(profit.grossProfit)}
                </p>
              </div>
              <div>
                <p className="text-zinc-400 text-xs">Economia Cliente</p>
                <p className={`font-semibold ${
                  customerSavings > 0 ? 'text-blue-400' : 'text-zinc-400'
                }`}>
                  {formatCurrency(customerSavings)}
                </p>
                {customerSavings > 0 && (
                  <p className="text-xs text-blue-400/70">
                    {formatPercentage(savingsPercentage)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Price to Charge */}
        <div className="text-right">
          <div className={`inline-flex flex-col items-end px-4 py-3 rounded-lg ${
            profit.exceedsAirlinePrice
              ? 'bg-red-500/10 border border-red-500/20'
              : 'bg-blue-500/10 border border-blue-500/20'
          }`}>
            <div className="flex items-center gap-1.5 mb-1">
              <DollarSign className={`w-4 h-4 ${
                profit.exceedsAirlinePrice ? 'text-red-400' : 'text-blue-400'
              }`} />
              <span className={`text-xs font-medium ${
                profit.exceedsAirlinePrice ? 'text-red-400' : 'text-blue-400'
              }`}>
                Cobrar do Cliente
              </span>
            </div>
            <p className={`text-2xl font-bold ${
              profit.exceedsAirlinePrice ? 'text-red-400' : 'text-blue-400'
            }`}>
              {formatCurrency(profit.chargedAmount)}
            </p>
            {profit.installments && profit.installments > 1 && (
              <p className="text-xs text-zinc-400 mt-1">
                {profit.installments}x de {formatCurrency(profit.chargedAmount / profit.installments)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Warnings */}
      {profit.exceedsAirlinePrice && (
        <div className="mt-3 pt-3 border-t border-red-800/30">
          <p className="text-xs text-red-400 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Preço acima do valor da companhia aérea - não recomendado
          </p>
        </div>
      )}
    </div>
  );
}

// Installment Card Component (Compact version for grid)
function InstallmentCard({
  profit,
  isBest,
  airlinePrice,
}: {
  profit: ProfitCalculation;
  isBest: boolean;
  airlinePrice: number;
}) {
  const installmentValue = profit.chargedAmount / (profit.installments || 1);
  const customerSavings = Math.max(0, airlinePrice - profit.chargedAmount);

  return (
    <div
      className={`relative p-3 rounded-lg border transition-all ${
        isBest
          ? 'bg-emerald-500/10 border-emerald-500/50'
          : profit.exceedsAirlinePrice
          ? 'bg-red-500/5 border-red-500/20'
          : 'bg-blue-500/5 border-blue-500/20'
      }`}
    >
      {isBest && (
        <Star className="absolute -top-1.5 -right-1.5 w-4 h-4 text-emerald-500 fill-emerald-500" />
      )}
      {profit.exceedsAirlinePrice && (
        <AlertTriangle className="absolute -top-1.5 -right-1.5 w-4 h-4 text-red-500" />
      )}
      <div className="space-y-2">
        {/* Header - Parcelas */}
        <div className="text-sm pb-2 font-semibold text-zinc-100">
          {profit.installments}x de {formatCurrency(installmentValue)}
        </div>

        {/* Grid - Cobrar, Lucro e Economia */}
        <div className="space-y-2 text-xs pt-2 border-t border-zinc-700/50">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-zinc-400 mb-0.5">Cobrar</p>
              <p className={`text-base font-bold ${
                profit.exceedsAirlinePrice ? 'text-red-400' : 'text-blue-400'
              }`}>
                {formatCurrency(profit.chargedAmount)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-zinc-400 mb-0.5">Lucro</p>
              <p className={`text-base font-bold ${
                getProfitColorClass(profit.grossProfit, profit.isProfitable)
              }`}>
                {formatCurrency(profit.grossProfit)}
              </p>
            </div>
          </div>
          <div className="pt-1 border-t border-zinc-700/30">
            <p className="text-zinc-400 mb-0.5">Economia Cliente</p>
            <p className={`text-sm font-semibold ${
              customerSavings > 0 ? 'text-blue-400' : 'text-zinc-400'
            }`}>
              {formatCurrency(customerSavings)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
